import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { URLService } from './url.service';
import { catchError, mergeMap, tap } from 'rxjs/operators';
import { Observable, ObservableInput, of, BehaviorSubject, PartialObserver } from 'rxjs';
import { ISession } from './session.interface';
import { JwkInterface } from './jwk.interface';

type SessionResponse = Partial<ISession>;

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private session = new BehaviorSubject<ISession | undefined>(undefined);
  private paymentLinkStatus = new BehaviorSubject<string | null>(null);

  constructor(
    private http: HttpClient,
    private urlService: URLService,
  ) {}

  public getCurrentSession(): ISession | undefined {
    return this.session.value;
  }

  public getSession(): Observable<ISession | undefined> {
    return this.session.asObservable();
  }

  public getPaymentLinkStatus(): Observable<string | null> {
    return this.paymentLinkStatus.asObservable();
  }

  public initSession(embedId: string) {
    this.http
      .get<SessionResponse>(this.urlService.session(embedId))
      .pipe(
        mergeMap(this.parseSessionResponse),
        catchError((err) => {
          console.error('Failed to fetch session:', err);
          return of(undefined);
        }),
      )
      .subscribe(this.session);
  }

  public initBlankSession(shortName: string) {
    this.http
      .get<SessionResponse>(this.urlService.blankSession(shortName))
      .pipe(
        mergeMap(this.parseSessionResponse),
        catchError((err) => {
          console.error('Failed to fetch session:', err);
          return of(undefined);
        }),
      )
      .subscribe(this.session);
  }

  public initRtpSession(token: string) {
    this.http
      .get<SessionResponse>(this.urlService.rtpSession(token))
      .pipe(
        mergeMap((response): ObservableInput<ISession | undefined> => {
          const status = response?.paymentLinkStatus;
          if (status === 'Paid' || status === 'Cancelled' || status === 'Expired') {
            this.paymentLinkStatus.next(status);
            return of(undefined);
          }
          return this.parseSessionResponse(response);
        }),
        catchError((err) => {
          console.error('Failed to fetch session:', err);
          return of(undefined);
        }),
      )
      .subscribe(this.session);
  }

  private async parseSessionResponse(
    response?: SessionResponse,
  ): Promise<ISession> {
    if (
      !response ||
      !response.sessionToken ||
      !response.publicKey ||
      !response.merchant
    ) {
      throw new Error('Invalid session');
    }

    const session: ISession = {
      publicKey: response.publicKey,
      sessionToken: response.sessionToken,
      merchant: response.merchant,
      provider: response.provider,
      region: response.region || 'CAN',
      authorizationString: `Bearer ${response.sessionToken}`,
      customerSession: response.customerSession,
    };

    return session;
  }
}
